import type { ErrorComponentProps } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { isSessionEndingRedirectInProgress } from 'shared/services';
import { captureSentryException } from 'shared/utils';

import { ErrorPage } from './components';
import { ERROR_PAGE_TYPES } from './consts';

interface Props extends ErrorComponentProps {
  /** Additional context to include in the Sentry extra payload. */
  extra?: Record<string, unknown>;
}

/** Renders a general error page when an unexpected error occurs */
export const GeneralError = ({ error, info, extra }: Props) => {
  const { t } = useTranslation();

  // A logout / session-expiry redirect cancels in-flight queries before its hard
  // `window.location.replace`; those aborted requests reject as generic errors
  // that can momentarily surface here. Skip the Sentry report and the error
  // screen for that teardown artifact — the page is already navigating away.
  const isSessionEnding = isSessionEndingRedirectInProgress();

  useEffect(() => {
    if (isSessionEnding) return;

    captureSentryException({
      message: 'General error page displayed',
      feature: 'errors',
      level: 'error',
      error,
      extra: {
        componentStack: info?.componentStack,
        ...extra
      }
    });
  }, [error, info, extra, isSessionEnding]);

  if (isSessionEnding) {
    return null;
  }

  return (
    <ErrorPage
      content={t('errors.generalErrorPage.content')}
      title={t('errors.generalErrorPage.title')}
      type={ERROR_PAGE_TYPES.INTERNAL_SERVER_ERROR_PAGE}
    />
  );
};
